/**
 * Bridge from the global assistant panel to the record that is open (#765,
 * spec §8.2). The panel lives in the layout; the audio player lives inside
 * `RecordDetailInline`. While a record is open it registers a host here, and
 * a click on a citation asks that host to play the moment. No host — the
 * panel deep-links to the record instead (§8.3).
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AssistantSubject } from '@aica/api-types/schemas';

/**
 * The CRM entity of the record on screen — what «ask about this deal» is
 * about (#765 Ф2). The three ids are the entity subject exactly as the API
 * keys it: `installationId` is `null`, never `undefined`, for a record
 * ingested through the Public API, because the thread key tells the two
 * apart and a missing key would merge them.
 */
export interface AssistantHostEntity {
  entityType: Extract<AssistantSubject, { scope: 'entity' }>['entityType'];
  entityId: string;
  installationId: string | null;
  /** The header's own wording for the entity (`formatCrmEntity`), as with `label`. */
  label: string;
}

export interface AssistantHost {
  recordId: string;
  /**
   * What to call this record on screen (#922) — «Входящий звонок · Иванов →
   * +380… ». Taken from the strings the record header already shows, so the
   * assistant cannot name a record differently from the page behind it, and
   * no second dictionary exists to drift. Presentation only: it never enters
   * `buildSubjectKey`, the panel's `key`, or any request.
   */
  label: string;
  /** How a quote is honoured: a call plays it, anything else scrolls the transcript to it. */
  recordType: 'call' | 'chat';
  /** The record's CRM entity, or `null` when it is not linked to one. */
  entity: AssistantHostEntity | null;
  /**
   * Play `clipSeconds` from `startSec` (calls) or scroll the transcript to
   * it (chats), switching to the transcript tab first when another tab is
   * open — the player is mounted only there. A request made before the
   * audio is decoded is queued by the host, never lost: the quote button is
   * never held back for it (owner's review, 17.09).
   */
  requestPlay: (startSec: number, clipSeconds: number) => void;
  /**
   * Open ANOTHER record where the host lives, at `atSec` when given. Set by a
   * host that has a place of its own for records (the CRM card's tab, #745):
   * a citation of another record opens there, instead of taking the page to
   * the dashboard. Without it, the panel deep-links (§8.3).
   */
  openRecord?: (recordId: string, atSec?: number) => void;
}

/** How another record opens where the surface has a place for records. */
export type AssistantRecordOpener = (recordId: string, atSec?: number) => void;

interface AssistantHostContextValue {
  host: AssistantHost | null;
  setHost: (host: AssistantHost | null) => void;
  recordOpener: AssistantRecordOpener | null;
  setRecordOpener: (opener: AssistantRecordOpener | null) => void;
}

const AssistantHostContext = createContext<AssistantHostContextValue | null>(null);

export function AssistantHostProvider({ children }: { children: ReactNode }) {
  const [host, setHost] = useState<AssistantHost | null>(null);
  // Set state with a function would call it: the opener is stored boxed.
  const [opener, setOpener] = useState<{ open: AssistantRecordOpener } | null>(null);
  const setRecordOpener = useCallback(
    (next: AssistantRecordOpener | null) => setOpener(next ? { open: next } : null),
    []
  );
  const value = useMemo(
    () => ({ host, setHost, recordOpener: opener?.open ?? null, setRecordOpener }),
    [host, opener, setRecordOpener]
  );
  return (
    <AssistantHostContext.Provider value={value}>
      {children}
    </AssistantHostContext.Provider>
  );
}

/** The record currently open, or `null` (no record, or outside the provider). */
export function useAssistantHost(): AssistantHost | null {
  return useContext(AssistantHostContext)?.host ?? null;
}

/** The registration door for `RecordDetailInline`; `null` outside the provider (tests, other hosts). */
export function useAssistantHostRegistry():
  ((host: AssistantHost | null) => void) | null {
  return useContext(AssistantHostContext)?.setHost ?? null;
}

/**
 * Where a quote of a record that is not open goes, for a surface that has a
 * place of its own for records (the CRM card's tab, #745) — registered for as
 * long as the surface is mounted, whether or not a record is open in it. The
 * open record's own `openRecord` wins; with neither the panel deep-links.
 */
export function useAssistantRecordOpener(): AssistantRecordOpener | null {
  return useContext(AssistantHostContext)?.recordOpener ?? null;
}

/** The registration door for such a surface; `null` outside the provider. */
export function useAssistantRecordOpenerRegistry():
  ((opener: AssistantRecordOpener | null) => void) | null {
  return useContext(AssistantHostContext)?.setRecordOpener ?? null;
}
